import React from 'react';
import { LumosMark } from './icons.js';

/**
 * Дверь в приложение: вход, ожидание одобрения устройства, «кто смотрит?».
 *
 * Слева — форма на тёмном, справа — вечерний пейзаж: небо в янтарь, солнце у
 * горизонта, слои холмов. Нарисован вектором прямо здесь: внешние картинки
 * клиенту запрещены (он ходит только на свой домен), а кадр из каталога до
 * входа взять неоткуда — каталог за дверью. Сцена статична и рисуется один
 * раз, поэтому градиенты в ней ничего не стоят.
 */
export function AuthScene({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <main className="auth-page">
      <div className="auth-scene" aria-hidden="true">
        <svg viewBox="0 0 1200 900" preserveAspectRatio="xMidYMid slice" focusable="false">
          <defs>
            <linearGradient id="auth-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#07080b" />
              <stop offset="0.38" stopColor="#1a120b" />
              <stop offset="0.62" stopColor="#5a3412" />
              <stop offset="0.74" stopColor="#c47a24" />
              <stop offset="0.8" stopColor="#f2b048" />
            </linearGradient>
            <radialGradient id="auth-sun" cx="0.62" cy="0.74" r="0.46">
              <stop offset="0" stopColor="#fff1c7" stopOpacity="0.95" />
              <stop offset="0.08" stopColor="#ffd27a" stopOpacity="0.85" />
              <stop offset="0.3" stopColor="#f8b840" stopOpacity="0.35" />
              <stop offset="1" stopColor="#f8b840" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="auth-cloud" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#2a1a10" stopOpacity="0.85" />
              <stop offset="1" stopColor="#2a1a10" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="auth-cloud-lit" cx="0.5" cy="0.6" r="0.5">
              <stop offset="0" stopColor="#d98a2e" stopOpacity="0.55" />
              <stop offset="1" stopColor="#d98a2e" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="auth-far" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#4a2a14" />
              <stop offset="1" stopColor="#21150d" />
            </linearGradient>
            <linearGradient id="auth-mid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#24170e" />
              <stop offset="1" stopColor="#100b08" />
            </linearGradient>
            <linearGradient id="auth-near" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#0d0a08" />
              <stop offset="1" stopColor="#07080b" />
            </linearGradient>
          </defs>
          <rect width="1200" height="900" fill="url(#auth-sky)" />
          <rect width="1200" height="900" fill="url(#auth-sun)" />
          {/* Облака — мягкие эллипсы: тёмные сверху, подсвеченные у горизонта. */}
          <ellipse cx="380" cy="250" rx="520" ry="90" fill="url(#auth-cloud)" />
          <ellipse cx="980" cy="180" rx="420" ry="70" fill="url(#auth-cloud)" />
          <ellipse cx="760" cy="420" rx="460" ry="46" fill="url(#auth-cloud-lit)" />
          <ellipse cx="300" cy="500" rx="380" ry="34" fill="url(#auth-cloud-lit)" />
          <ellipse cx="1040" cy="520" rx="300" ry="26" fill="url(#auth-cloud-lit)" />
          {/* Солнце у самого горизонта. */}
          <circle cx="744" cy="664" r="38" fill="#ffe6a6" />
          {/* Холмы — три плана, от светлого дальнего к чёрному переднему. */}
          <path
            d="M0 690 C120 640 220 660 330 620 C450 576 560 640 690 650 C820 660 900 600 1010 606 C1100 610 1160 640 1200 650 V900 H0 Z"
            fill="url(#auth-far)"
          />
          <path
            d="M0 760 C140 700 260 720 380 690 C520 654 600 720 760 724 C900 728 980 680 1100 692 C1150 697 1180 708 1200 716 V900 H0 Z"
            fill="url(#auth-mid)"
          />
          <path
            d="M0 830 C160 780 300 800 470 772 C640 744 760 800 920 796 C1040 793 1130 770 1200 776 V900 H0 Z"
            fill="url(#auth-near)"
          />
          {/* Ели на ближнем холме — силуэтом, как на кадре у горизонта. */}
          <g fill="#07080b">
            <path d="M880 800 l18 -70 l18 70 Z M886 770 l12 -48 l12 48 Z" />
            <path d="M920 804 l14 -52 l14 52 Z" />
            <path d="M960 798 l22 -92 l22 92 Z M968 760 l14 -60 l14 60 Z" />
            <path d="M1010 796 l12 -40 l12 40 Z" />
            <path d="M1040 792 l18 -74 l18 74 Z" />
            <path d="M1084 788 l13 -46 l13 46 Z" />
          </g>
        </svg>
      </div>
      <section className={['auth-card', className ?? ''].filter(Boolean).join(' ')}>
        <div className="auth-brand">
          <LumosMark className="brand-mark" />
          Lumos
        </div>
        {children}
      </section>
    </main>
  );
}
